import { useState } from "react";
import styles from "./image.module.scss";
import { Box, Skeleton } from "@mui/material";
import classnames from "classnames";
import PropTypes from "prop-types";
import NextImage from "next/image";
import { height } from "@mui/system";

export function Image({ alt, children, src, blurDataURL, ...props }) {
  const { className, loading, hidden, onClick, ...rest } = props;

  const [hasError, setHasError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (hidden) return null;

  const commonProps = {
    alt: alt || src?.split("/").at(-1)?.split(".")[0],
    height: props.height,
    width: props.width,
  };

  const imageProps = {
    ...commonProps,
    className: classnames(styles.image, className, {
      [styles.error]: hasError,
    }),
    onClick: onClick,
    onError: () => setHasError(true),
    onLoad: () => setLoaded(true),
    src,
    ...rest,
  };

  if (!src || hasError) {
    return (
      <Skeleton
        component="img"
        className={imageProps.className}
        variant="rectangular"
      />
    );
  }

  if (blurDataURL) {
    return (
      <Box className={styles.root}>
        {blurDataURL && (
          <img
            {...commonProps}
            key={blurDataURL}
            className={classnames(imageProps.className, styles.blur)}
            loading="eager"
            quality={10}
            src={blurDataURL}
            style={{ opacity: loaded ? 0 : 1 }}
          />
        )}
        <NextImage
          {...imageProps}
          key={src}
          blurDataURL={blurDataURL}
          loading={loading || "lazy"}
          placeholder="blur"
          quality={100}
          style={{ opacity: loaded ? 1 : 0 }}
          unoptimized={true}
        />
      </Box>
    );
  }

  return <NextImage key={src} {...imageProps} loading={loading || "eager"} />;
}

Image.propTypes = {
  alt: PropTypes.string,
  className: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
  src: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
